<template>
    <div>
        <div class="d-flex justify-content-between">
            <h4 class="mb-1 table-title">Trading Account Exposure</h4>
            <div class="d-flex">
                <span class="text-danger mr-1 cursor-pointer" v-if="isFilterShown" @click="clearFilter"
                    >Clear Search</span
                >
                <font-awesome-icon
                    :icon="['far', 'filter']"
                    class="btn-icon mr-1"
                    v-b-tooltip.hover.top="'Filter'"
                    @click="toggleFilter"
                />
                <font-awesome-icon
                    :icon="['far', 'compress-arrows-alt']"
                    class="btn-icon mr-1"
                    v-b-tooltip.hover.top="`Filter out 0 values: ${isFilterZeroShown ? 'on' : 'off'}`"
                    @click="toggleFilterZero"
                />
                <font-awesome-icon
                    :icon="['far', 'file-export']"
                    class="btn-icon"
                    v-b-tooltip.hover.top="'Export CSV'"
                    @click="$bvModal.show('trading-account-download-modal')"
                />
                <b-modal
                    id="trading-account-download-modal"
                    title="Choose format type"
                    centered
                    @ok="exportFile(exportData, exportFormat, 'TradingAccountExposure')"
                >
                    <v-select v-model="exportFormat" :clearable="false" label="format" :options="['csv', 'xls']" />
                </b-modal>
            </div>
        </div>

        <b-card no-body class="px-50 rounded my-shadow table-16-rows">
            <CommonTable
                :isLoading="isLoading"
                :fields="fields"
                :items="dataAfterCollapsed"
                :max-display-rows="19"
                :has-summary-row="false"
                :isFilterShown="isFilterShown"
                :isFilterZeroShown="isFilterZeroShown"
                @filterChange="filterChange"
                @filterClear="filterClear"
                :canFilterOutZeroValues="canFilterOutZeroValues"
                @filterOutZeroValues="handleFilterOutZeroValues"
                :field_sort="field_sort"
                :isDesc="isDesc"
                @onHeaderClick="(field) => sortBy(field)"
                ref="table"
            >
                <template #cell(name)="{ item }">
                    <div :style="{ 'padding-left': `${calcNamePadding(item.level)}px` }">
                        <dot v-if="isItemUncollapsible(item)" />
                        <span v-else class="cursor-pointer">
                            <font-awesome-icon
                                :icon="['fas', 'caret-down']"
                                size="lg"
                                class="mr-50"
                                @click="collapse(item)"
                                v-if="!collapsedSet.includes(item[whichpath])"
                            />
                            <font-awesome-icon
                                :icon="['fas', 'caret-right']"
                                size="lg"
                                class="mr-50"
                                @click="uncollapse(item)"
                                v-else
                            />
                        </span>
                        <NameField :name="item.name" />
                    </div>
                </template>
                <template #cell(Risk)="{item}">
                    <div :class="riskStyle(item.Risk)" style="width: 10px; height: 10px; border-radius: 5px" />
                </template>
            </CommonTable>
            <div class="py-1 pr-1 d-flex justify-content-between card-footer-color">
                <FilterPills
                    :isLoading="isLoading"
                    v-model="activeLevels"
                    :levels="levels"
                    @toggleLevel="toggleLevel"
                />

                <div class="my-auto text-headings-color">Items: {{ dataAfterCollapsed.length }}</div>
            </div>
        </b-card>
    </div>
</template>
<script>
import CommonTable from "@/components/tables/CommonTable";
import Ripple from "vue-ripple-directive";
import axios from "@axios";
import {
    formatMoney,
    exportFile,
    formatNumber,
    setFilterInLocalStorage,
    getFilterInLocalStorage,
    removeFilterInLocalStorage,
    isFilteredByRegion,
    getClientEmail,
    mergeTwoClients,
    symbolOfCurrency,
} from "@/helper";
import moment from "moment";
import Dot from "@/components/Dot";
import tradingAccountFields from "./fields/tradingAccountFields.js";
import NameField from "@/components/tables/NameField.vue";
import FilterPills from "@/components/tables/LevelPills.vue";
import vSelect from "vue-select";
import riskStyle from "@/utils/riskStyle";
import { mapActions, mapGetters } from "vuex";

const TABLE_NAME = "TradingAccountExposure";

export default {
    components: {
        Dot,
        CommonTable,
        NameField,
        FilterPills,
        vSelect,
    },

    directives: {
        Ripple,
    },

    props: {},

    data() {
        return {
            exportFormat: "csv",
            tradingAccountFields,
            tradingAccountFields,
            isLoading: false,

            labels: ["NetPositionLots", "NetExposure", "Risk"],
            numberLabels: ["NetPositionLots", "NetExposure"],

            isDesc: true,
            field_sort: "NetExposure",

            activeLevels: [],
            collapsedSet: [],

            isFilterShown: false,
            isFilterZeroShown: true,
            filterValues: {},

            canFilterOutZeroValues: true,
            filterOutZeroValues: {},

            group_organization: {},
        };
    },

    computed: {
        ...mapGetters({
            trading_accounts: "tableData/market_exposure",
            risk_threshold: "tableData/risk_threshold",
            groupLevels: "tableData/group_levels",
        }),

        companyCurrency() {
            const riskMangementSettings = this.$store.getters["companySettings/settingsByPath"](["risk_management"]);
            if (riskMangementSettings.settings) return riskMangementSettings.settings.currency;
            return "";
        },
        fields() {
            return this.tradingAccountFields;
        },

        fieldsMap() {
            return this.fields.reduce((res, val) => {
                return Object.assign(res, { [val.key]: val });
            }, {});
        },

        levels() {
            let result = this.groupLevels.map((levelName, index) => ({
                name: levelName,
                value: (item) => item.path[index],
            }));
            return [
                ...result,
                { name: "Client", value: (item) => `${item.properties.user}` },
                {
                    name: "Account",
                    value: (item) => `${item.properties.server}-${item.properties.login}-${item.properties.symbol}`,
                },
            ];
        },

        whichpath() {
            let res = "path";
            if (!isFilteredByRegion(this.activeLevels, this.group_level)) {
                res = "subpath";
            }
            return res;
        },

        dataTotal() {
            let result = this.calculateSum(
                this.numberLabels,
                this.dataAfterGroup.filter((item) => item.level == 1)
            );
            return result;
        },

        dataAfterCollapsed() {
            let results = JSON.parse(JSON.stringify(this.dataAfterFormat));
            this.collapsedSet.forEach((path) => {
                results = results.filter((item) => !this.isChildOfParent(item[this.whichpath], path));
            });
            // console.log(this.collapsedSet, results)
            return results;
        },

        dataAfterFormat() {
            let results = JSON.parse(JSON.stringify(this.dataAfterFilteredByZero));
            results = results.map((item) => {
                this.fields.forEach(({ key }) => {
                    item[key] = this.formatValue(item, key);
                });
                return item;
            });
            // console.log(results)
            return results;
        },

        dataAfterFilteredByZero() {
            let results = JSON.parse(JSON.stringify(this.dataAfterFiltered));

            Object.keys(this.filterOutZeroValues).forEach((key) => {
                if (this.filterOutZeroValues[key]) {
                    results = results.filter((item) => {
                        if (key != "Risk") return !isNaN(parseFloat(item[key])) && parseFloat(item[key]) !== 0;
                        else return item[key] !== 1;
                    });
                }
            });
            let validPaths = results.map((item) => item[this.whichpath]);
            results = JSON.parse(JSON.stringify(this.dataAfterSort));
            results = results.filter((item) => {
                let bool = false;
                validPaths.forEach((path) => {
                    if (this.isChildOfParent(path, item[this.whichpath]) || path == item[this.whichpath]) {
                        bool = true;
                        return;
                    }
                });
                return bool;
            });
            return results;
        },
        dataAfterFiltered() {
            let results = JSON.parse(JSON.stringify(this.dataAfterSort));
            Object.keys(this.filterValues).forEach((key) => {
                let field = this.fields.find((field) => field.key == key);
                if (field?.filter?.filterFunc) {
                    // console.log(results, this.filterValues[key])
                    results = field.filter.filterFunc(results, this.filterValues[key]);
                } else {
                    if (this.filterValues[key]) {
                        results = results.filter((item) => {
                            return (this.formatValue(item, key) ?? "")
                                .toString()
                                .toLowerCase()
                                .includes(this.filterValues[key].toString().toLowerCase());
                        });
                    }
                }
            });
            let validPaths = results.map((item) => item[this.whichpath]);
            results = JSON.parse(JSON.stringify(this.dataAfterSort));
            results = results.filter((item) => {
                let bool = false;
                validPaths.forEach((path) => {
                    if (this.isChildOfParent(path, item[this.whichpath]) || path == item[this.whichpath]) {
                        bool = true;
                        return;
                    }
                });
                return bool;
            });
            return results;
        },

        dataAfterSort() {
            let desc_or_asc = this.isDesc ? -1 : 1;
            let result = [];
            let j = 0;
            this.activeLevels.forEach((level, index) => {
                let pos_or_neg = index == 0 ? desc_or_asc : -desc_or_asc;
                let data = this.dataAfterGroup.filter((datum) => datum.level == level);
                data.sort((a, b) => {
                    let x = a[this.field_sort];
                    let y = b[this.field_sort];
                    if (this.fieldsMap[this.field_sort].displayAbsolute) {
                        x = Math.abs(x);
                        y = Math.abs(y);
                    }
                    return pos_or_neg * (x < y ? -1 : x > y ? 1 : 0);
                });

                // Check if table is filtered by region, if not we have to merge client with same email (diffrent region)
                if (
                    !isFilteredByRegion(this.activeLevels, this.groupLevels.length) &&
                    level === this.groupLevels.length + 1
                ) {
                    let noDuplicatedData = {};
                    data.forEach((datum) => {
                        if (noDuplicatedData[datum.name]) {
                            noDuplicatedData[datum.name] = mergeTwoClients(
                                noDuplicatedData[datum.name],
                                datum,
                                this.fields
                            );
                        } else noDuplicatedData[datum.name] = datum;
                    });
                    data = Object.values(noDuplicatedData);
                }

                if (index == 0) {
                    result = result.concat(data);
                    j = level;
                } else {
                    // Find correct index and insert the value
                    // console.log(level, index, j, data)
                    data.forEach((current) => {
                        result.forEach((datum, index) => {
                            if (
                                datum.level == j &&
                                this.isChildOfParent(current[this.whichpath], datum[this.whichpath])
                            ) {
                                result.splice(index + 1, 0, current);
                            }
                        });
                    });
                    j = level;
                }
            });
            // console.log("dataAfterSort", results)
            return result;
        },

        dataAfterGroup() {
            let results = [];
            // console.log(this.items)

            this.levels.forEach((level, index) => {
                if (this.activeLevels.includes(index + 1)) {
                    let distinctValues = [
                        ...new Set(
                            this.items.map((item) => {
                                let path = item.path.split("/").slice(0, index + 1);
                                if (this.whichpath == "subpath") {
                                    path = path.slice(Math.min(...this.activeLevels) - 1);
                                }
                                return path.join("/");
                            })
                        ),
                    ];

                    // console.log(distinctValues)

                    results.push(
                        ...distinctValues.map((path) => {
                            let name = path.split("/");
                            name = this.group_organization[path]?.name ?? name[name.length - 1];
                            // console.log(path, this.items.map(item => item[this.whichpath]))
                            let data = this.items.filter(
                                (item) =>
                                    this.isChildOfParent(item[this.whichpath], path) || item[this.whichpath] == path
                            );
                            // console.log(path, data)
                            let tmp = this.calculateSum(this.numberLabels, data);

                            if (index == this.levels.length - 1) {
                                tmp = {
                                    ...data[0],
                                    ...tmp,
                                };
                                // console.log(index, data, tmp)
                            }

                            // console.log(level, path)

                            return {
                                name: name,
                                ...tmp,
                                level: index + 1,
                                isUncollapsible: index + 1 === Math.max(...this.activeLevels),
                                [this.whichpath]: path,
                            };
                        })
                    );
                }
            });
            // console.log("dataAfterGroup", results)
            return results;
        },

        items() {
            let result = this.trading_accounts
                .filter((item) => item.value.NetPositionLots && item.value.NetExposure)
                .map((item) => {
                    let values = JSON.parse(JSON.stringify(item.value));
                    let tmp = this.labels.reduce((obj, key) => {
                        obj[key] = values?.[key] ?? NaN;
                        return obj;
                    }, {});
                    // console.log(item)
                    let path = this.levels.map((level) =>
                        level.value({ ...item, path: item.properties.region.split("/") })
                    );
                    let subpath = path.slice(Math.min(...this.activeLevels) - 1);

                    return {
                        ...tmp,
                        name: item.properties.server + "-" + item.properties.login,
                        path: path.join("/"),
                        subpath: subpath.join("/"),
                        level: this.levels.length,
                        symbol: item.properties.symbol,
                    };
                });
            return result;
        },

        exportData() {
            return [
                [...this.fields.map((field) => field.label)],
                ...this.items.map((item) => {
                    return this.fields.map((field) => {
                        if (!isNaN(item[field.key])) return formatNumber(item[field.key], 2);
                        return item[field.key];
                    });
                }),
            ];
        },
    },

    methods: {
        formatMoney,
        moment,
        exportFile,
        riskStyle,
        symbolOfCurrency,

        isChildOfParent(child, parent) {
            let child_path = child.split("/");
            let parent_path = parent.split("/");
            return (
                child_path.length > parent_path.length && child_path.slice(0, parent_path.length).join("/") == parent
            );
        },

        initFilter() {
            if (getFilterInLocalStorage(TABLE_NAME)) {
                let filterValues = getFilterInLocalStorage(TABLE_NAME);

                this.isFilterShown = true;
                this.$refs.table.initFilter(filterValues);
            }
            if (getFilterInLocalStorage(TABLE_NAME + "Zero")) {
                let filterValues = getFilterInLocalStorage(TABLE_NAME + "Zero");
                this.isFilterZeroShown = filterValues.show ? true : false;
                this.$refs.table.initFilterZero(filterValues);
            }
        },

        toggleFilter() {
            this.isFilterShown = !this.isFilterShown;
            if (this.isFilterShown) {
                let filterValues = getFilterInLocalStorage(TABLE_NAME);
                setFilterInLocalStorage(TABLE_NAME, {});
            } else {
                removeFilterInLocalStorage(TABLE_NAME);
            }
        },

        toggleFilterZero() {
            this.isFilterZeroShown = !this.isFilterZeroShown;
            if (this.isFilterZeroShown) {
                let filterOutZeroValues = getFilterInLocalStorage(TABLE_NAME + "Zero") || {};

                setFilterInLocalStorage(TABLE_NAME + "Zero", {
                    ...filterOutZeroValues,
                    show: true,
                });
            } else {
                let filterOutZeroValues = getFilterInLocalStorage(TABLE_NAME + "Zero") || {};

                setFilterInLocalStorage(TABLE_NAME + "Zero", {
                    ...filterOutZeroValues,
                    show: false,
                });
            }
        },
        clearFilter() {
            this.$refs.table.clearFilter();
        },

        filterChange(filterValues) {
            Object.keys(filterValues).forEach((key) => {
                this.$set(this.filterValues, key, filterValues[key]);
            });
            if (this.isFilterShown) setFilterInLocalStorage(TABLE_NAME, filterValues);
        },

        filterClear(filterValues) {
            Object.keys(filterValues).forEach((key) => {
                this.$set(this.filterValues, key, filterValues[key]);
            });
            if (this.isFilterShown) setFilterInLocalStorage(TABLE_NAME, filterValues);
        },

        handleFilterOutZeroValues(filterOutZeroValues) {
            Object.keys(filterOutZeroValues).forEach((key) => {
                if (key != "show") this.$set(this.filterOutZeroValues, key, filterOutZeroValues[key]);
            });
            if (this.isFilterZeroShown)
                setFilterInLocalStorage(TABLE_NAME + "Zero", {
                    ...filterOutZeroValues,
                    show: this.isFilterZeroShown,
                });
        },

        sortBy(field) {
            if (field) {
                this.field_sort = field;
                this.isDesc = !this.isDesc;
            }
        },

        formatValue(item, field) {
            switch (field) {
                case "name":
                    return item.name;
                case "symbol":
                    return item.symbol;
                case "NetPositionLots":
                    return item.NetPositionLots ? formatMoney(item.NetPositionLots, 2) : "";
                case "NetExposure":
                    return item.NetExposure
                        ? `${symbolOfCurrency(this.companyCurrency)}${formatMoney(item.NetExposure, 0)}`
                        : "";
                case "Risk":
                    return item.Risk;
            }
        },

        calcNamePadding(level) {
            let a = [...this.TARegionFilterSet, 4, 5];
            a.sort();
            return 25 * a.indexOf(level);
        },

        calculateSum(numberLabels, data) {
            return numberLabels.reduce((obj, key) => {
                if (data.length == 1) {
                    obj[key] = data[0][key];
                } else {
                    obj[key] = data.reduce((sum, item) => sum + (isNaN(item[key]) ? 0 : item[key]), 0);
                }
                return obj;
            }, {});
        },

        isItemUncollapsible(item) {
            return item.level == Math.max(...this.activeLevels);
        },

        collapse(item) {
            item.isCollapsed = true;
            this.collapsedSet.push(item[this.whichpath]);
        },

        uncollapse(item) {
            item.isCollapsed = false;
            this.collapsedSet = this.collapsedSet.filter((path) => path != item[this.whichpath]);
        },

        calcNamePadding(level) {
            return 25 * this.activeLevels.indexOf(level);
        },

        toggleLevel(level) {
            // console.log(level, this.activeLevels)
            this.collapsedSet = this.collapsedSet.filter((path) => path.split("/").length >= level);
        },
        exportCSV() {
            const link = document.createElement("a");
            data = [
                `Name, Account, Time, Type, Symbol, Volume, Reason, Swap, Profit, Currency, Profit (${this.companyCurrency})`,
                ...this.mt_deals.map((item) => {
                    return [
                        item.id,
                        item.login,
                        moment(item.timestamp).format("YYYY.MM.DD HH:mm:ss"),
                        this.action[item.action],
                        item.symbol,
                        item.volume,
                        item.reason,
                        item.storage,
                        item.profit,
                        this.companyCurrency,
                        item.profit,
                    ].join(",");
                }),
            ].join("\n");

            let blob = new Blob([data], { type: "text/csv;charset=utf-8;" });
            let url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", `${metric_name}${moment().format("x")}.csv`);
            link.click();
        },

        fetch_market_exposure() {
            this.isLoading = true;
            this.$store
                .dispatch("tableData/fetchMarketExposure")
                .then((res) => {})
                .finally(() => {
                    this.isLoading = false;
                });
        },

        getAllChildren(node, path) {
            let result = { [path]: node.settings };
            if (node.children) {
                Object.keys(node.children).forEach((key) => {
                    result = { ...result, ...this.getAllChildren(node.children[key], path + "/" + key) };
                });
            }
            return result;
        },
    },

    async mounted() {
        this.initFilter();

        this.fetch_market_exposure();

        this.$store.dispatch("tableData/fetchRiskThreshold");

        await this.$store.dispatch("tableData/fetchGroupLevels");

        await this.$nextTick();
        this.activeLevels = [1, 2, 3, this.levels.length];

        axios
            // .get(`${process.env.VUE_APP_API_URL}site_settings/risk_management/group_organization?recursive`)
            //? mockData
            .get("/GroupOrganization/data")
            .then((res) => {
                this.group_organization = {};
                if (res.data?.children) {
                    Object.keys(res.data.children).forEach((key) => {
                        this.group_organization = {
                            ...this.group_organization,
                            ...this.getAllChildren(res.data.children[key], key),
                        };
                    });
                }
            })
            .catch((err) => console.error(err));
    },
};
</script>
